import React, {CSSProperties, useMemo, useState} from 'react'
import styled from 'styled-components'
import {POLICY_ROOT_MENU_WIDTH} from 'consts'
import {useTranslation} from 'i18n'
import {LegalNavBarOption, WindowModeType} from 'types'
import {useWindowMode} from 'windows'
import convertUnit from 'lib/unit'
import {PolicyRootNavBar, PolicyTopNavBar} from '../containers'
import {PolicyRootScreenProps} from './PolicyRootScreenProps'

interface StyledSectionChildrenProps {
  mode: WindowModeType
  header?: boolean
  hideMarginTop?: boolean
}

const StyledSectionContent = styled.div`
  overflow-x: hidden;
  display: flex;
  position: relative;
  flex: 1;
  flex-direction: column;
`

const StyledSectionChildren = styled.div<StyledSectionChildrenProps>`
  ${({header, mode}) => ({
    paddingLeft: !header
      ? undefined
      : convertUnit(mode === 'website' ? POLICY_ROOT_MENU_WIDTH : 0),
  })}
  margin-top: 0;
  display: flex;
  flex: 1;
`

const StyledChildrenBaseContainer = styled.div`
  flex: 1;
  position: relative;
`

const StyledChildrenContainer = styled.div<CSSProperties>`
  background-color: ${({theme, backgroundColor}) =>
    backgroundColor ? theme[backgroundColor] : theme.white_1};
  display: flex;
  position: absolute;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
  align-items: center;
  justify-content: center;
`

const StyledChildrenContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  z-index: 1;
`

export default function PolicyRootScreen({
  children,
  childrenContainerStyle,
  hideBottomNavbar = false,
  clickState,
  onClick,
  showHeader,
  hideMarginTop = false,
}: PolicyRootScreenProps) {
  const {translate} = useTranslation()
  const mode = useWindowMode()
  const [expand, setExpand] = useState(false)
  const currentState = clickState[0]
  const navbarOptions = useMemo<LegalNavBarOption[]>(
    () => [
      {
        key: 'how-it-works',
        label: translate('global:howItWorks'),
        onClick: () => {
          onClick('how-it-works')
        },
      },
      {
        key: 'yuser',
        label: 'Yuser',
        onClick: () => {
          onClick('yuser')
        },
      },
      {
        key: 'creator',
        label: translate('giftShop:creator'),
        onClick: () => {
          onClick('creator')
        },
      },
      {
        key: 'host',
        label: translate('giftShop:host'),
        onClick: () => {
          onClick('host')
        },
      },
      {
        key: 'privacy',
        label: translate('policy:privacyPolicy'),
        onClick: () => {
          onClick('privacy')
        },
      },
      {
        key: 'terms-of-use',
        label: translate('policy:termsOfUse'),
        onClick: () => {
          onClick('terms-of-use')
        },
      },
      {
        key: 'community-guidelines',
        label: translate('policy:communityGuideline'),
        onClick: () => {
          onClick('community-guidelines')
        },
      },
      {
        key: 'biometric-face-recognition',
        label: translate('policy:biometricPolicy'),
        onClick: () => {
          onClick('biometric-face-recognition')
        },
      },
      {
        key: 'copyright',
        label: translate('policy:copyright'),
        onClick: () => {
          onClick('copyright')
        },
      },
      {
        key: 'faq',
        label: translate('global:faq'),
        onClick: () => {
          onClick('faq')
        },
      },
      {
        key: 'help-center',
        label: translate('policy:helpCenter'),
        onClick: () => {
          onClick('help-center')
        },
      },
      {
        key: 'tutorials',
        label: translate('policy:tutorials'),
        onClick: () => {
          onClick('tutorials')
        },
      },
      {
        key: 'refund',
        label: translate('policy:refundPolicy'),
        onClick: () => {
          onClick('refund')
        },
      },
      {
        key: 'cookie',
        label: translate('policy:cookie'),
        onClick: () => {
          onClick('cookie')
        },
      },
      {
        key: 'underage-user-policy',
        label: translate('policy:privacyPolicyChild'),
        onClick: () => {
          onClick('underage-user-policy')
        },
      },
      {
        key: 'supergiga',
        label: translate('policy:supergiga'),
        onClick: () => {
          onClick('supergiga')
        },
      },
    ],
    [onClick, translate],
  )

  const handleRenderSideNavBar = useMemo(
    () =>
      mode === 'website' && (
        <PolicyRootNavBar
          select={currentState}
          data={navbarOptions}
          expand={expand}
        />
      ),
    [currentState, expand, mode, navbarOptions],
  )

  const handleRenderTopNavBar = useMemo(
    () =>
      mode !== 'website' &&
      !hideBottomNavbar && (
        <PolicyTopNavBar
          expand={expand}
          select={currentState}
          data={navbarOptions}
          onExpand={() => setExpand((previous) => !previous)}
        />
      ),
    [currentState, expand, hideBottomNavbar, mode, navbarOptions],
  )

  return (
    <StyledSectionContent>
      {showHeader === true ? handleRenderTopNavBar : null}
      {showHeader === true ? handleRenderSideNavBar : null}
      <StyledSectionChildren
        header={showHeader}
        mode={mode}
        hideMarginTop={hideMarginTop}>
        <StyledChildrenBaseContainer>
          <StyledChildrenContainer style={childrenContainerStyle}>
            <StyledChildrenContentContainer>
              {children}
            </StyledChildrenContentContainer>
          </StyledChildrenContainer>
        </StyledChildrenBaseContainer>
      </StyledSectionChildren>
    </StyledSectionContent>
  )
}
